import { type Taxon } from '../api/generated/notification-pref';

type ChannelsMap = Record<
  string,
  {
    channels: string[];
    name?: string;
    defaultOn?: boolean;
  }
>;

/**
 * Type guard to check if a Taxon node is a leaf (has channels) vs a branch
 */
function isNotificationClass(node: Taxon): node is Taxon {
  // leaf nodes carry a channels list; branch nodes carry nested classes
  return 'channels' in node || !('metadata' in node && node.metadata && 'icon' in node.metadata);
}

/**
 * Recursively get all channels for a given notification class node and its sub-classes
 * Works with the new schema where:
 * - Keys in the classes object are the full class identifiers (e.g., "ciam.domain.created")
 * - Leaf nodes have channels array and metadata
 * - Branch nodes only have nested classes
 * @param node - The notification class node to traverse
 * @param result - Accumulator for the channels map
 */
export function getChannelsPerClass(node: Taxon, result: ChannelsMap = {}): ChannelsMap {
  if (node.classes && Object.keys(node.classes).length > 0) {
    for (const [key, sub] of Object.entries(node.classes)) {
      // Check if this is a leaf node (has channels)
      if (isNotificationClass(sub) && sub.channels) {
        // Use the key directly as the class name - it's already the full identifier
        result[key] = {
          channels: sub.channels,
          name: sub.metadata?.name,
          defaultOn: !!sub.default_channels?.length,
        };
      } else {
        // Branch node - recurse deeper
        getChannelsPerClass(sub, result);
      }
    }
  }

  return result;
}

/**
 * Given the notification class name, find all channels that are associated with it
 * Works with the new schema structure where keys are full class identifiers
 * @param tree - The root notification class node
 * @param classNameTarget - The class name to find (should be the full identifier like "ciam.domain.created")
 */
export function findChannelsForNotificationClass(
  tree: Taxon,
  classNameTarget: string
): string[] | undefined {
  if (tree.classes) {
    for (const [key, subClass] of Object.entries(tree.classes)) {
      // Check if this key matches the target
      if (key === classNameTarget || key.endsWith(`.${classNameTarget}`)) {
        if (isNotificationClass(subClass) && subClass.channels) {
          return subClass.channels;
        }
      }
      // Recurse into nested classes
      const result = findChannelsForNotificationClass(subClass, classNameTarget);
      if (result) return result;
    }
  }

  return undefined;
}
